import { useEffect, useState } from "react";
import axios from "axios";
import { Quote, Loader2, Send, MessageSquareQuote, PlayCircle, Star } from "lucide-react";
import { toast } from "sonner";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", location: "", text: "", rating: 5 };

function StarRating({ value, onChange, size = 5 }) {
  return (
    <div className="flex gap-1" data-testid="testimonial-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange && onChange(n)}
          data-testid={`rating-star-${n}`}
          className="focus:outline-none"
          aria-label={`${n} estrelas`}
        >
          <Star className={`w-${size} h-${size} ${n <= value ? "fill-[#FF6A00] text-[#FF6A00]" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Testimonials() {
  const settings = useSiteSettings();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  const sectionTitle = settings.testimonials_title?.trim() ? settings.testimonials_title : "O Que Dizem os Nossos Clientes";
  const sectionDescription = settings.testimonials_description?.trim() || "";
  const galleryTitle = settings.gallery_title?.trim() ? settings.gallery_title : "Galeria de Clientes";
  const galleryDescription = settings.gallery_description?.trim() || "";
  const galleryItems = (settings.gallery_items || []).filter((g) => g.published && g.media_url);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/testimonials`);
        setTestimonials(res.data);
      } catch {
        /* keep empty */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/testimonials`, form);
      toast.success("Obrigado! O seu testemunho foi enviado e ficará visível após aprovação.");
      setForm(emptyForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível enviar. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="testemunhos" data-testid="testimonials-section" className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-semibold tracking-[0.35em] uppercase text-slate-500 mb-4">
            Testemunhos
          </p>
          <h2
            data-testid="testimonials-heading"
            className="font-display text-3xl sm:text-4xl tracking-tight text-[#0072C6]"
          >
            {sectionTitle}
          </h2>
          {sectionDescription && (
            <p data-testid="testimonials-description" className="mt-4 text-base leading-relaxed text-slate-500 whitespace-pre-line">
              {sectionDescription}
            </p>
          )}
        </div>

        {/* Galeria de Clientes */}
        {galleryItems.length > 0 && (
          <div className="mb-16" data-testid="gallery-section">
            <div className="max-w-2xl mb-8">
              <h3 className="font-display text-2xl sm:text-3xl tracking-tight text-[#0072C6]">{galleryTitle}</h3>
              {galleryDescription && (
                <p className="mt-3 text-sm leading-relaxed text-slate-500 whitespace-pre-line">{galleryDescription}</p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
              {galleryItems.map((g) => (
                <figure key={g.id} data-testid={`gallery-card-${g.id}`} className="bg-[#F8FAFC] border border-slate-200 overflow-hidden flex flex-col">
                  {g.media_type === "video" ? (
                    <div className="relative bg-black">
                      <video src={g.media_url} controls preload="metadata" className="w-full h-56 object-cover" data-testid={`gallery-video-${g.id}`} />
                    </div>
                  ) : (
                    <img src={g.media_url} alt={g.name || "Cliente"} className="w-full h-56 object-cover" loading="lazy" data-testid={`gallery-image-${g.id}`} />
                  )}
                  {(g.name || g.description) && (
                    <figcaption className="p-5 flex-1">
                      {g.name && (
                        <p className="font-semibold text-sm text-[#0072C6] flex items-center gap-2">
                          {g.media_type === "video" && <PlayCircle className="w-4 h-4" />}
                          {g.name}
                        </p>
                      )}
                      {g.description && <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{g.description}</p>}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12 text-slate-400" data-testid="testimonials-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : testimonials.length === 0 ? (
          <div
            data-testid="testimonials-empty"
            className="bg-[#F8FAFC] border border-slate-200 p-10 sm:p-14 text-center mb-14"
          >
            <MessageSquareQuote className="w-10 h-10 text-[#0072C6]/30 mx-auto mb-4" />
            <p className="font-display text-xl text-[#0072C6] mb-2">Ainda não há testemunhos publicados</p>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Já comprou ou importou uma viatura connosco? Seja o primeiro a partilhar a sua experiência
              usando o formulário abaixo.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 mb-14" data-testid="testimonials-grid">
            {testimonials.map((t) => (
              <figure
                key={t.id}
                data-testid={`testimonial-card-${t.id}`}
                className="bg-[#F8FAFC] border border-slate-200 p-8 flex flex-col"
              >
                <Quote className="w-8 h-8 text-[#0072C6]/25 mb-5" />
                <blockquote className="text-sm leading-relaxed text-slate-700 flex-1">
                  “{t.text}”
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-slate-200">
                  <div className="flex items-center gap-1 mb-1.5" data-testid={`testimonial-stars-${t.id}`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= (t.rating || 5) ? "fill-[#FF6A00] text-[#FF6A00]" : "text-slate-300"}`} />
                    ))}
                  </div>
                  <p className="font-semibold text-sm text-[#0072C6]">{t.name}</p>
                  {t.location && <p className="text-xs text-slate-500 mt-0.5">{t.location}</p>}
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        {/* Submission form */}
        <div className="max-w-2xl" data-testid="testimonial-form-wrapper">
          <h3 className="font-display text-2xl text-[#0072C6] mb-2">Deixe o Seu Testemunho</h3>
          <p className="text-sm text-slate-500 mb-6">
            A sua opinião será revista pela nossa equipa antes de ser publicada no site.
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="testimonial-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="t-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Nome *
                </label>
                <input
                  id="t-name"
                  data-testid="testimonial-name-input"
                  required
                  minLength={2}
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]"
                  placeholder="O seu nome"
                />
              </div>
              <div>
                <label htmlFor="t-location" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Localidade
                </label>
                <input
                  id="t-location"
                  data-testid="testimonial-location-input"
                  maxLength={120}
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6]"
                  placeholder="Ex.: Lisboa"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                A sua classificação *
              </label>
              <StarRating value={form.rating} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} />
            </div>
            <div>
              <label htmlFor="t-text" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                O seu testemunho *
              </label>
              <textarea
                id="t-text"
                data-testid="testimonial-text-input"
                required
                minLength={10}
                maxLength={1000}
                rows={5}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] resize-y"
                placeholder="Conte-nos como foi a sua experiência (mínimo 10 caracteres)"
              />
            </div>
            <button
              type="submit"
              data-testid="testimonial-submit-button"
              disabled={sending}
              className="inline-flex items-center gap-2 bg-[#FF6A00] hover:bg-[#e65f00] disabled:opacity-60 text-white text-sm font-semibold px-7 py-3 transition-colors"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar Testemunho
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
